/**
 * Upgrade prompt component for progressive enhancement
 */

import React, { useState } from 'react';

export interface UpgradeLevel {
  level: number;
  name: string;
  description: string;
  features: string[];
  setupTime?: string;
  setupSteps?: string[];
}

export interface UpgradePromptProps {
  currentLevel: number;
  nextLevel: UpgradeLevel;
  onUpgrade?: () => void;
  onDismiss?: () => void;
  onLearnMore?: () => void;
}

export function UpgradePrompt({
  currentLevel,
  nextLevel,
  onUpgrade,
  onDismiss,
  onLearnMore,
}: UpgradePromptProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="fixed bottom-4 right-4 max-w-md bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Unlock {nextLevel.name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {nextLevel.description}
            </p>
          </div>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="ml-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Dismiss"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Features list */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            New features:
          </h4>
          <ul className="space-y-1">
            {nextLevel.features.map((feature, index) => (
              <li key={index} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {/* Setup time */}
        {nextLevel.setupTime && (
          <div className="mb-4 flex items-center text-sm text-gray-600 dark:text-gray-400">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Setup time: {nextLevel.setupTime}
          </div>
        )}

        {/* Expandable setup steps */}
        {nextLevel.setupSteps && nextLevel.setupSteps.length > 0 && (
          <div className="mb-4">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
            >
              <svg
                className={`w-4 h-4 mr-1 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              {isExpanded ? 'Hide' : 'Show'} setup steps
            </button>
            
            {isExpanded && (
              <ol className="mt-2 space-y-1 list-decimal list-inside">
                {nextLevel.setupSteps.map((step, index) => (
                  <li key={index} className="text-sm text-gray-600 dark:text-gray-400">
                    {step}
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3">
          {onUpgrade && (
            <button
              onClick={onUpgrade}
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Upgrade Now
            </button>
          )}
          {onLearnMore && (
            <button
              onClick={onLearnMore}
              className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Learn More
            </button>
          )}
          {!onUpgrade && !onLearnMore && onDismiss && (
            <button
              onClick={onDismiss}
              className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Maybe Later
            </button>
          )}
        </div>
      </div>

      {/* Progress indicator */}
      <div className="h-1 bg-gray-200 dark:bg-gray-700">
        <div
          className="h-full bg-blue-500 transition-all duration-300"
          style={{ width: `${((currentLevel + 1) / 4) * 100}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Mini upgrade banner for less intrusive prompting
 */
export interface UpgradeBannerProps {
  message: string;
  actionText?: string;
  onAction?: () => void;
  onDismiss?: () => void;
}

export function UpgradeBanner({
  message,
  actionText = 'Upgrade',
  onAction,
  onDismiss,
}: UpgradeBannerProps) {
  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 px-4 py-2">
      <div className="flex items-center justify-between">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          {message}
        </p>
        <div className="flex items-center gap-2">
          {onAction && (
            <button
              onClick={onAction}
              className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              {actionText}
            </button>
          )}
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              aria-label="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}